import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import contractService, { invalidateContractTemplatesCache } from '../../../services/contractService';
import toast from 'react-hot-toast';

const SendContract = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        pjo_id: [],
        contract_name: '',
        subject: '',
        contract_template: '',
        message: '',
        remark: ''
    });

    const [pjos, setPjos] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pjosLoading, setPjosLoading] = useState(true);
    const [templatesLoading, setTemplatesLoading] = useState(true);
    const [templatesError, setTemplatesError] = useState(null);

    const [errors, setErrors] = useState({});
    const [activeTooltip, setActiveTooltip] = useState(null);

    // Signed-document preview state (only used in edit mode).
    // `signedDocument` is delivered directly by GET /api/v1/admin/contracts/:id so we
    // can embed the S3-presigned PDF URL without a separate /download round-trip.
    const [contractDetail, setContractDetail] = useState(null);
    const [signatureStatus, setSignatureStatus] = useState(null);
    const [signedDocument, setSignedDocument] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const isContractComplete =
        signatureStatus?.is_complete === true ||
        contractDetail?.status === 'Completed';

    const showSignedDocumentSection =
        isEdit &&
        (signatureStatus || isContractComplete || signedDocument?.fileUrl);
    
    // Searchable dropdown state
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filteredPjos, setFilteredPjos] = useState([]);

    const applyContractDetail = useCallback((detailResponse) => {
        if (!detailResponse?.success) return;

        const {
            contract,
            signatureStatus: sigStatus,
            signedDocument: sigDoc,
        } = detailResponse.data;

        setFormData({
            pjo_id: (contract.contractPeople || [])
                .map((cp) => String(cp.pjo_id ?? cp.user?.id ?? ''))
                .filter(Boolean),
            contract_name: contract.contract_name || '',
            subject: contract.subject || '',
            contract_template: contract.contract_template || '',
            message: contract.message || '',
            remark: contract.remark || '',
        });
        setContractDetail(contract || null);
        setSignatureStatus(sigStatus || null);
        setSignedDocument(sigDoc || null);
    }, []);

    const loadTemplates = useCallback(async ({ cancelled = () => false, force = false } = {}) => {
        setTemplatesLoading(true);
        setTemplatesError(null);

        if (force) {
            invalidateContractTemplatesCache();
        }

        try {
            const templatesResponse = await contractService.getTemplates();
            if (cancelled()) return;

            if (!templatesResponse?.success) {
                const message = templatesResponse?.message || 'Failed to load contract templates';
                setTemplatesError(message);
                toast.error(message);
                return;
            }

            const templateData = Array.isArray(templatesResponse.data)
                ? templatesResponse.data
                : (templatesResponse.data?.templates || []);
            setTemplates(templateData);
        } catch (err) {
            if (cancelled()) return;
            const message = err?.message || 'Failed to load contract templates';
            setTemplatesError(message);
            toast.error(message);
        } finally {
            if (!cancelled()) {
                setTemplatesLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        let active = true;

        setPjosLoading(true);
        setTemplatesLoading(true);
        setTemplatesError(null);

        // Each call resolves independently so state updates as soon as each
        // response arrives — form fields and dropdowns don't wait on each other.
        contractService.getAirliners()
            .then((response) => {
                if (!active) return;
                if (response?.success) {
                    const formattedPjos = (response.data || []).map(pjo => ({
                        id: String(pjo.id),
                        label: `${pjo.first_name}${pjo.last_name ? ' ' + pjo.last_name : ''} (${pjo.email})`
                    }));
                    setPjos(formattedPjos);
                } else {
                    toast.error('Failed to load Airliners');
                }
            })
            .catch(() => {
                if (active) toast.error('Failed to load Airliners');
            })
            .finally(() => {
                if (active) setPjosLoading(false);
            });

        contractService.getTemplates()
            .then((templatesResponse) => {
                if (!active) return;
                if (templatesResponse?.success) {
                    const templateData = Array.isArray(templatesResponse.data)
                        ? templatesResponse.data
                        : (templatesResponse.data?.templates || []);
                    setTemplates(templateData);
                } else {
                    const message = templatesResponse?.message || 'Failed to load contract templates';
                    setTemplatesError(message);
                    toast.error(message);
                }
            })
            .catch((err) => {
                if (!active) return;
                const message = err?.message || 'Failed to load contract templates';
                setTemplatesError(message);
                toast.error(message);
            })
            .finally(() => {
                if (active) setTemplatesLoading(false);
            });

        if (isEdit) {
            contractService.getDetail(id)
                .then((detailResponse) => {
                    if (!active) return;
                    applyContractDetail(detailResponse);
                })
                .catch(() => {
                    if (active) toast.error('Failed to load contract details');
                });
        }

        return () => {
            active = false;
        };
    }, [isEdit, id, applyContractDetail]);

    // Live Dropbox Sign signer status — loaded separately so DB form fields appear first.
    useEffect(() => {
        if (!isEdit || !id || !contractDetail?.signature_request_id) {
            return;
        }

        let active = true;

        const loadLiveSignatureStatus = async () => {
            try {
                const response = await contractService.getSignatureStatus(id);
                if (active && response?.success && response.data) {
                    setSignatureStatus(response.data);
                }
            } catch {
                // Signer table is optional; form data is already from DB.
            }
        };

        loadLiveSignatureStatus();

        return () => {
            active = false;
        };
    }, [isEdit, id, contractDetail?.signature_request_id]);

    // When the contract is complete but the fast detail response has no embedded PDF
    // (S3 backfill still running), fetch the download URL in a separate long-running call.
    useEffect(() => {
        if (!isEdit || !id || !isContractComplete || signedDocument?.fileUrl) {
            return;
        }

        let active = true;

        const loadSignedPdfPreview = async () => {
            setDownloading(true);
            try {
                const response = await contractService.download(id);
                if (!active) return;

                if (response?.success && response.data?.fileUrl) {
                    setSignedDocument({
                        fileUrl: response.data.fileUrl,
                        source: response.data.source || 's3',
                        s3Key: null,
                        available: true,
                    });
                }
            } catch {
                // Preview is optional; the manual download button remains available.
            } finally {
                if (active) {
                    setDownloading(false);
                }
            }
        };

        loadSignedPdfPreview();

        return () => {
            active = false;
        };
    }, [isEdit, id, isContractComplete, signedDocument?.fileUrl]);

    // Filter PJOs based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = pjos.filter(pjo => 
                pjo.label.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredPjos(filtered);
        } else {
            setFilteredPjos(pjos);
        }
    }, [searchTerm, pjos]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.searchable-dropdown')) {
                setIsDropdownOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Specific logic for template selection
        if (name === 'contract_template' && value) {
            const selectedTemplate = templates.find(t => (t.templateId || t.template_id) === value);
            if (selectedTemplate) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    // Auto-fill subject and message if they are empty
                    subject: prev.subject || selectedTemplate.subject || '',
                    message: prev.message || selectedTemplate.message || ''
                }));
                // Clear errors
                setErrors(prev => ({ ...prev, [name]: null, subject: null, message: null }));
                return;
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleBack = () => {
        navigate('/admin/contracts');
    };

    const handleSelectSigner = (pjoId) => {
        setFormData(prev => ({ ...prev, pjo_id: [pjoId] }));
        setIsDropdownOpen(false);
        setSearchTerm('');
        if (errors.pjo_id) {
            setErrors(prev => ({ ...prev, pjo_id: null }));
        }
    };

    const getSelectedSignerLabel = () => {
        if (!formData.pjo_id || formData.pjo_id.length === 0) return '';
        const selectedPjo = pjos.find(p => p.id === formData.pjo_id[0]);
        if (selectedPjo) return selectedPjo.label;

        const signerUser = contractDetail?.contractPeople?.[0]?.user;
        if (signerUser) {
            return `${signerUser.first_name}${signerUser.last_name ? ` ${signerUser.last_name}` : ''} (${signerUser.email})`;
        }
        return '';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.pjo_id || formData.pjo_id.length === 0) newErrors.pjo_id = "Please select a signer.";
        if (!formData.contract_name) newErrors.contract_name = "The Contract name field is required.";
        if (!formData.subject) newErrors.subject = "The Subject field is required.";
        if (!formData.contract_template) newErrors.contract_template = "The Contract template field is required.";
        if (!formData.message) newErrors.message = "The Message field is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (shouldNavigate = true, saveAndSend = false) => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);
        try {
            if (isEdit) {
                // For updates, send all modifiable fields including signers
                const updateData = {
                    pjo_id: formData.pjo_id,
                    contract_name: formData.contract_name,
                    subject: formData.subject,
                    message: formData.message,
                    save_and_send: saveAndSend
                };
                const result = await contractService.update(id, updateData);
                if (result.success) {
                    toast.success(saveAndSend ? 'Contract sent successfully' : 'Contract saved successfully');
                } else {
                    throw new Error(result.message || 'Failed to update contract');
                }
            } else {
                const createData = {
                    ...formData,
                    save_and_send: saveAndSend
                };
                const result = await contractService.create(createData);
                if (result.success) {
                    toast.success(saveAndSend ? 'Contract sent successfully' : 'Contract saved successfully');
                } else {
                    throw new Error(result.message || 'Failed to create contract');
                }
            }
            if (shouldNavigate) {
                navigate('/admin/contracts');
            }
        } catch (err) {
            const apiMessage = err?.response?.data?.message || err?.message || 'Failed to save contract';
            if (String(apiMessage).toLowerCase().includes('void and recreate')) {
                toast.error('Contract already viewed. Void and recreate required.');
            } else {
                toast.error(apiMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadSigned = async () => {
        if (!id) return;
        setDownloading(true);
        try {
            const response = await contractService.download(id);
            if (response?.success && response.data?.fileUrl) {
                window.open(response.data.fileUrl, '_blank', 'noopener,noreferrer');
            } else {
                throw new Error(response?.message || 'Download URL unavailable');
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to download signed document');
        } finally {
            setDownloading(false);
        }
    };

    const renderSignerStatusBadge = (status) => {
        const normalized = (status || '').toLowerCase();
        const cls =
            normalized === 'signed'
                ? 'bg-green-100 text-green-700'
                : normalized === 'declined'
                ? 'bg-red-100 text-red-700'
                : normalized === 'awaiting_signature'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-600';
        return (
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${cls}`}>
                {(status || 'unknown').replace(/_/g, ' ')}
            </span>
        );
    };

    const Tooltip = ({ text, id }) => (
        <div
            className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#333] text-white text-[11px] rounded shadow-lg whitespace-nowrap z-[60] transition-opacity duration-200 ${activeTooltip === id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#333]"></div>
        </div>
    );

    return (
        <div className="p-4 bg-[#f4f7f6] min-h-screen font-sans">
            {/* Breadcrumb */}
            <div className="mb-3 flex items-center gap-2 text-[12px] text-gray-500 font-medium">
                <i className="fas fa-file-contract"></i>
                <span>Contracts</span>
                <i className="fas fa-chevron-right text-[8px]"></i>
                <span>{isEdit ? 'Edit Contract Details' : 'Send Contract'}</span>
            </div>

            <div className="bg-white rounded shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-800">{isEdit ? 'Edit Contract Details' : 'Send Contract'}</h2>
                    </div>
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-sm transition-colors uppercase tracking-tight"
                    >
                        <i className="fas fa-arrow-left"></i>
                        <span>Back</span>
                    </button>
                </div>

                {/* Form Container */}
                <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 mb-4">
                        {/* Select Signer (Searchable Dropdown) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Select Signer<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('pjo_id')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Select the Airliner (PJO) who needs to sign." id="pjo_id" />
                                </div>
                            </label>
                            
                            {/* Custom Searchable Dropdown */}
                            <div className="relative searchable-dropdown">
                                {/* Selected Value Display */}
                                <div
                                    onClick={() => !pjosLoading && setIsDropdownOpen(!isDropdownOpen)}
                                    className={`w-full px-3 py-2 bg-white border ${errors.pjo_id ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] cursor-pointer text-sm font-medium h-[38px] flex items-center justify-between ${pjosLoading ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                >
                                    <span className={getSelectedSignerLabel() ? 'text-gray-900' : 'text-gray-400'}>
                                        {pjosLoading && !getSelectedSignerLabel()
                                            ? 'Loading Airliners...'
                                            : (getSelectedSignerLabel() || 'Select Signer')}
                                    </span>
                                    <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                                </div>

                                {/* Dropdown Menu */}
                                {isDropdownOpen && !pjosLoading && (
                                    <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-[300px] overflow-hidden">
                                        {/* Search Input */}
                                        <div className="p-2 border-b border-gray-200 bg-white">
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Search..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-[#ffeb00] focus:outline-none"
                                                autoFocus
                                            />
                                        </div>

                                        {/* Options List - Scrollable */}
                                        <div className="overflow-y-auto max-h-[240px] overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                                            {filteredPjos.length > 0 ? (
                                                <>
                                                    {/* Placeholder option */}
                                                    <div
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, pjo_id: [] }));
                                                            setIsDropdownOpen(false);
                                                            setSearchTerm('');
                                                        }}
                                                        className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {/* Select Signer */}
                                                    </div>
                                                    {filteredPjos.map(pjo => (
                                                        <div
                                                            key={pjo.id}
                                                            onClick={() => handleSelectSigner(pjo.id)}
                                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-yellow-300 ${formData.pjo_id[0] === pjo.id ? 'bg-yellow-300 font-semibold' : 'text-gray-700'}`}
                                                        >
                                                            {pjo.label}
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <div className="px-3 py-2 text-sm text-gray-400 text-center">
                                                    No results found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {errors.pjo_id && <span className="text-[11px] text-red-500 font-medium">{errors.pjo_id}</span>}
                        </div>

                        {/* Contract Title / Name */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                {isEdit ? 'Contract Name/Title' : 'Contract Name'}<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('title')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Contract name will be included in the signature request." id="title" />
                                </div>
                            </label>
                            <input
                                type="text"
                                name="contract_name"
                                placeholder={isEdit ? 'Contract name/title' : 'Contract name'}
                                value={formData.contract_name}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 bg-white border ${errors.contract_name ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all`}
                            />
                            {errors.contract_name && <span className="text-[11px] text-red-500 font-medium">{errors.contract_name}</span>}
                        </div>

                        {/* Subject */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Subject<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('subject')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Subject will be send as signature email subject." id="subject" />
                                </div>
                            </label>
                            <input
                                type="text"
                                name="subject"
                                placeholder="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 bg-white border ${errors.subject ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all`}
                            />
                            {errors.subject && <span className="text-[11px] text-red-500 font-medium">{errors.subject}</span>}
                        </div>

                        {/* Contract Template (Required) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Contract Template<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('template')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Select a HelloSign template for the contract." id="template" />
                                </div>
                            </label>
                            <select
                                name="contract_template"
                                value={formData.contract_template}
                                onChange={handleChange}
                                disabled={templatesLoading}
                                className={`w-full px-3 py-2 bg-white border ${errors.contract_template || templatesError ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all ${templatesLoading ? 'bg-gray-50 cursor-not-allowed' : ''} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_10px_center] bg-no-repeat`}
                            >
                                <option value="">
                                    {templatesLoading && !formData.contract_template
                                        ? 'Loading templates...'
                                        : templatesError
                                            ? 'Failed to load templates'
                                            : 'Select template'}
                                </option>
                                {templates.map(tmp => (
                                    <option key={tmp.templateId || tmp.template_id} value={tmp.templateId || tmp.template_id}>
                                        {tmp.title || 'Untitled Template'}
                                    </option>
                                ))}
                                {isEdit && formData.contract_template && !templates.some(t => (t.templateId || t.template_id) === formData.contract_template) && (
                                    <option value={formData.contract_template}>
                                        {formData.contract_template}
                                    </option>
                                )}
                            </select>
                            {templatesError && (
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-[11px] text-red-500 font-medium">{templatesError}</span>
                                    <button
                                        type="button"
                                        onClick={() => loadTemplates({ force: true })}
                                        disabled={templatesLoading}
                                        className="text-[11px] font-bold text-blue-600 hover:underline disabled:opacity-50"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                            {errors.contract_template && <span className="text-[11px] text-red-500 font-medium">{errors.contract_template}</span>}
                        </div>
                    </div>

                    {/* Message */}
                    <div className="flex flex-col gap-1 mb-6">
                        <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                            Message<span className="text-red-500">*</span>
                            <div className="relative">
                                <i
                                    className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                    onMouseEnter={() => setActiveTooltip('message')}
                                    onMouseLeave={() => setActiveTooltip(null)}
                                ></i>
                                <Tooltip text="Message will be send as signature email message." id="message" />
                            </div>
                        </label>
                        <textarea
                            name="message"
                            placeholder="Message"
                            rows={2}
                            value={formData.message}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-white border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium min-h-[60px]`}
                        ></textarea>
                        {errors.message && <span className="text-[11px] text-red-500 font-medium">{errors.message}</span>}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end gap-3 mt-8">
                        <button
                            onClick={() => handleSave(true, true)}
                            disabled={loading}
                            className={`px-8 py-2.5 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-md transition-all flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading && <i className="fas fa-circle-notch fa-spin"></i>}
                            <span>Save And Send</span>
                        </button>
                        <button
                            onClick={() => handleSave(true, false)}
                            disabled={loading}
                            className={`px-8 py-2.5 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-md transition-all flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading && <i className="fas fa-circle-notch fa-spin"></i>}
                            <span>Save</span>
                        </button>
                        <button
                            onClick={handleBack}
                            className="px-8 py-2.5 text-sm font-bold text-gray-700 bg-[#e9ecef] hover:bg-gray-200 rounded shadow-sm transition-all border border-gray-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Signed Document Preview (edit mode only) */}
            {showSignedDocumentSection && (
                <div className="bg-white rounded shadow-sm overflow-hidden mt-4">
                    <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-bold text-gray-800">Signed Document</h2>
                            <span
                                className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                                    isContractComplete
                                        ? 'bg-green-100 text-green-700'
                                        : signatureStatus?.is_declined
                                        ? 'bg-red-100 text-red-700'
                                        : 'bg-blue-100 text-blue-700'
                                }`}
                            >
                                {isContractComplete
                                    ? 'Completed'
                                    : signatureStatus?.is_declined
                                    ? 'Declined'
                                    : 'In Progress'}
                            </span>
                            {signatureStatus?.test_mode && (
                                <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-bold uppercase tracking-wide">
                                    Test Mode
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleDownloadSigned}
                                disabled={downloading}
                                className={`px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded shadow-sm transition-colors flex items-center gap-2 ${downloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                {downloading ? (
                                    <i className="fas fa-circle-notch fa-spin"></i>
                                ) : (
                                    <i className="fas fa-download"></i>
                                )}
                                Download PDF
                            </button>
                        </div>
                    </div>

                    {/* Signers list */}
                    {Array.isArray(signatureStatus?.signatures) && signatureStatus.signatures.length > 0 && (
                        <div className="px-6 py-4 border-b border-gray-100">
                            <h3 className="text-[11px] font-bold text-gray-400 tracking-wider mb-3 uppercase">
                                Signers
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-100">
                                            <th className="py-2 pr-4 font-bold">Order</th>
                                            <th className="py-2 pr-4 font-bold">Name</th>
                                            <th className="py-2 pr-4 font-bold">Email</th>
                                            <th className="py-2 pr-4 font-bold">Role</th>
                                            <th className="py-2 pr-4 font-bold">Status</th>
                                            <th className="py-2 pr-4 font-bold">Signed At</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {signatureStatus.signatures.map((sig, idx) => (
                                            <tr key={sig.signature_id || idx} className="hover:bg-gray-50">
                                                <td className="py-2 pr-4 text-gray-600 font-medium">
                                                    {typeof sig.order === 'number' ? sig.order + 1 : idx + 1}
                                                </td>
                                                <td className="py-2 pr-4 text-gray-800 font-semibold">
                                                    {sig.signer_name || '—'}
                                                </td>
                                                <td className="py-2 pr-4 text-gray-600">
                                                    {sig.signer_email_address || '—'}
                                                </td>
                                                <td className="py-2 pr-4 text-gray-600">
                                                    {sig.signer_role || '—'}
                                                </td>
                                                <td className="py-2 pr-4">
                                                    {renderSignerStatusBadge(sig.status_code)}
                                                </td>
                                                <td className="py-2 pr-4 text-gray-600 text-xs">
                                                    {sig.signed_at
                                                        ? new Date(sig.signed_at * 1000).toLocaleString()
                                                        : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Embedded signed PDF preview — sourced from GET /admin/contracts/:id */}
                    <div className="p-6">
                        {!isContractComplete ? (
                            <div className="border border-dashed border-gray-300 rounded p-8 text-center bg-gray-50">
                                <i className="fas fa-hourglass-half text-gray-400 text-4xl mb-3"></i>
                                <p className="text-sm text-gray-600 font-medium mb-1">
                                    The signed document isn't available yet.
                                </p>
                                <p className="text-xs text-gray-500">
                                    It will be embedded here once all signers have completed the document.
                                </p>
                            </div>
                        ) : signedDocument?.fileUrl ? (
                            <>
                                <div className="flex items-center justify-between mb-2 text-[11px] text-gray-500 font-medium">
                                    <span>
                                        Source:{' '}
                                        <span className={signedDocument.source === 's3' ? 'text-green-700' : 'text-blue-700'}>
                                            {signedDocument.source === 's3' ? 'JetScanner S3' : 'Dropbox Sign'}
                                        </span>
                                    </span>
                                    <a
                                        href={signedDocument.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-gray-600 hover:text-black transition-colors"
                                    >
                                        <i className="fas fa-external-link-alt"></i>
                                        Open in new tab
                                    </a>
                                </div>
                                <div className="border border-gray-200 rounded overflow-hidden bg-gray-50">
                                    <iframe
                                        title={`Signed document - ${formData.contract_name || id}`}
                                        src={signedDocument.fileUrl}
                                        className="w-full"
                                        style={{ height: '720px', border: 'none' }}
                                    />
                                </div>
                            </>
                        ) : downloading ? (
                            <div className="border border-dashed border-gray-300 rounded p-8 text-center bg-gray-50">
                                <i className="fas fa-spinner fa-spin text-gray-400 text-3xl mb-3"></i>
                                <p className="text-sm text-gray-600 font-medium">
                                    Loading signed document preview…
                                </p>
                            </div>
                        ) : (
                            <div className="border border-dashed border-gray-300 rounded p-8 text-center bg-gray-50">
                                <p className="text-sm text-gray-500 font-medium">
                                    No signed document is available for this contract.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SendContract;
